import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as discountsActions } from "reducers/discounts";
import * as i18n from "util/i18n";
import { actions as notificationActions } from "reducers/notification";
import { push } from "connected-react-router";
import * as config from "util/config";
import gaEventTracker, { events } from "util/gaEventTracker";

const useDiscountCreation = () => {
    const dispatch = useDispatch();
    const {
        fetching,
        fetchingSimulation,
        discountSimulationStatus,
        listEcheq,
        hasPartials,
        listEcheqResponse,
    } = useSelector((_state) => _state.discounts);

    const { activeEnvironment } = useSelector((_state) => _state.session);

    const allowedForDiscounts = listEcheqResponse?.calificacion || 0;

    const MAX_ECHEQS_SELECTED = Number(config.get("echeqs.discounts.maxEcheqsPerBatch") || 25);
    const [selectedEcheqs, setSelectedEcheqs] = useState([]);
    const [totalAmountSelected, setTotalAmountSelected] = useState(0);
    const [allEcheqsAreSelected, setAllEcheqsAreSelected] = useState(false);

    const { accounts } = useSelector((_state) => _state.accounts);
    const [availableAccounts, setAvailableAccounts] = useState([{ id: "1" }]);
    const [selectedAccount, setSelectedAccount] = useState("");

    useEffect(() => {
        if (accounts?.length) {
            const formatOption = (account) => ({
                balance: { currency: account?.currency, quantity: account?.availableBalance },
                currency: account?.currency,
                frequentDestination: null,
                id: account?.productId,
                label: `${i18n.get(`productType.${account?.accountType}`)} ····${account?.accountNumber?.slice(-4)}`,
                minimumPayment: 0,
                permissions: Object.keys(account?.permissions),
                statementsLoan: null,
                totalPayment: 0,
                type: account?.accountType,
            });

            setAvailableAccounts(accounts.filter((acc) => acc.currency === "ARS")?.map((acc) => formatOption(acc)));
        }
    }, [accounts]);

    useEffect(() => {
        if (discountSimulationStatus === "SUCCESS") {
            dispatch(discountsActions.resetDiscountSimulationStatus());
            dispatch(push("/discounts/simulation"));
        }
    }, [discountSimulationStatus]);

    const handleCheckEcheq = (echeqId, checked) => {
        // refresco sesion
        dispatch(discountsActions.refreshSession());

        if (checked && !selectedEcheqs.includes(echeqId)) {
            if (selectedEcheqs.length === MAX_ECHEQS_SELECTED) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("echeq.discount.validation.limit", "", {
                            MAX_ECHEQS_SELECTED,
                        }),
                        "warning",
                        ["discountsPage"],
                    ),
                );
            } else {
                // valido si es echeq PROPIOS o DE TERCEROS
                if (selectedEcheqs?.length) {
                    const firstEcheq = listEcheq.find((echeq) => echeq.cheque_id === selectedEcheqs[0]);
                    const currentEcheq = listEcheq.find((echeq) => echeq.cheque_id === echeqId);

                    if (
                        firstEcheq &&
                        currentEcheq &&
                        firstEcheq?.emisor_cuit === activeEnvironment?.activeEnvironmentDocument &&
                        firstEcheq?.emisor_cuit !== currentEcheq?.emisor_cuit
                    ) {
                        dispatch(
                            notificationActions.showNotification(
                                i18n.get("echeq.discount.validation.singleType"),
                                "warning",
                                ["discountsPage"],
                            ),
                        );

                        return;
                    }
                }

                const newSelectedEcheqs = [...selectedEcheqs, echeqId];
                setSelectedEcheqs(newSelectedEcheqs);

                const newAllEcheqsAreSelected = !listEcheq.some(
                    (echeq) => !newSelectedEcheqs.includes(echeq.cheque_id),
                );
                if (newAllEcheqsAreSelected) {
                    setAllEcheqsAreSelected(true);
                }

                const newEcheqAmount = listEcheq.find((echeq) => echeq.cheque_id === echeqId)?.monto;

                setTotalAmountSelected((lv) => lv + (newEcheqAmount || 0));
            }
        } else if (!checked && selectedEcheqs.includes(echeqId)) {
            setSelectedEcheqs((lv) => lv.filter((echeqIdSaved) => echeqIdSaved !== echeqId));
            setAllEcheqsAreSelected(false);

            const newEcheqAmount = listEcheq.find((echeq) => echeq.cheque_id === echeqId)?.monto;
            setTotalAmountSelected((lv) => lv - (newEcheqAmount || 0));
        }
    };

    const setInitialApprovedEcheqs = (approvedEcheqs) => {
        const arrayTemp = [];
        let amount = 0;
        approvedEcheqs.forEach((e) => {
            const idx = listEcheq.findIndex((v) => v?.cheque_id === e);
            if (idx !== -1) {
                arrayTemp.push(e);
                amount += listEcheq[idx].monto || 0;
            }
        });
        setSelectedEcheqs(arrayTemp);
        setTotalAmountSelected(amount);
    };

    const handleCheckAllEcheqs = (checked) => {
        if (checked) {
            // valido si es echeq PROPIOS o DE TERCEROS
            const arrCuitEmisor = listEcheq.map((echeq) => echeq.emisor_cuit);
            const setCuitEmisor = new Set(arrCuitEmisor);
            if (setCuitEmisor?.size > 1) {
                dispatch(
                    notificationActions.showNotification(i18n.get("echeq.discount.validation.singleType"), "warning", [
                        "discountsPage",
                    ]),
                );

                return;
            }

            let newTotalAmountSelected = 0;
            const newSelectedEcheqs = [];
            listEcheq.forEach((echeq, i) => {
                if (i < MAX_ECHEQS_SELECTED) {
                    newTotalAmountSelected += echeq.monto;
                    newSelectedEcheqs.push(echeq.cheque_id);
                }
            });
            setTotalAmountSelected(newTotalAmountSelected);
            setSelectedEcheqs(newSelectedEcheqs);

            if (listEcheq.length <= MAX_ECHEQS_SELECTED) {
                setAllEcheqsAreSelected(true);
            } else {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("echeq.discount.validation.limit", "", {
                            MAX_ECHEQS_SELECTED,
                        }),
                        "warning",
                        ["discountsPage"],
                    ),
                );
            }
        } else {
            setSelectedEcheqs([]);
            setAllEcheqsAreSelected(false);
            setTotalAmountSelected(0);
        }
    };

    const checkLotType = () => {
        if (selectedEcheqs?.length) {
            const firstEcheq = listEcheq.find((echeq) => echeq.cheque_id === selectedEcheqs[0]);

            return firstEcheq && firstEcheq.emisor_cuit === activeEnvironment?.activeEnvironmentDocument;
        }

        return false;
    };

    const submit = () => {
        const { type, currency } = availableAccounts.find((acc) => acc.id === selectedAccount);

        gaEventTracker.triggerEvent(
            events.echeq_simular({
                datetime: new Date(),
                montoTotal: totalAmountSelected,
                cantChequesSeleccionados: selectedEcheqs.length,
            }),
        );

        dispatch(discountsActions.setApprovedEcheqs([]));

        dispatch(
            discountsActions.loadDiscountSimulationRequest({
                echeqList: selectedEcheqs.reduce((newFormat, echeqId) => [...newFormat, { cheque_id: echeqId }], []),
                account: {
                    idProduct: selectedAccount,
                    productType: `${type}-${currency}`,
                },
                isOwnEcheq: checkLotType(),
            }),
        );
    };

    useEffect(() => {
        if (totalAmountSelected > allowedForDiscounts) {
            dispatch(
                notificationActions.showNotification(i18n.get("echeq.discount.amount.validation.limit"), "warning", [
                    "discountsPage",
                ]),
            );
        }
    }, [totalAmountSelected]);

    return {
        fetching,
        fetchingSimulation,

        allowedForDiscounts,
        hasPartials,

        allEcheqsAreSelected,
        listEcheq,
        selectedEcheqs,
        setSelectedEcheqs,
        setAllEcheqsAreSelected,
        totalAmountSelected,
        setTotalAmountSelected,
        handleCheckEcheq,
        handleCheckAllEcheqs,

        availableAccounts,
        selectedAccount,
        setSelectedAccount,

        submit,

        setInitialApprovedEcheqs,
    };
};

export default useDiscountCreation;
